/**
 * NavigationWheel (Feature Compass) — Circular Section Controller
 * 
 * Responsive circular compass. Items arranged radially around center hub.
 * Center = Health Intelligence. Items toggle section visibility.
 * Starts expanded by default. User can collapse for minimal view.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, Lock, Check } from 'lucide-react';
import { brand } from '../../styles/brand';

const { colors } = brand;

const TIER_META = {
  free: { color: '#00E5A0', label: 'Free' },
  smart: { color: '#3B82F6', label: 'Smart' },
  genius: { color: '#A855F7', label: 'Genius' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION DEFINITIONS PER SCREEN
// ═══════════════════════════════════════════════════════════════════════════════

export const HOME_SECTIONS = [
  { tier: 'free', items: [
    { id: 'todays-meals', label: 'Meals', sub: 'LOG & EDIT' },
    { id: 'activity', label: 'Activity', sub: 'STEPS & EXERCISE' },
  ]},
  { tier: 'smart', items: [
    { id: 'checkin', label: 'Check-in', sub: 'SLEEP & ENERGY' },
    { id: 'micros', label: 'Micros', sub: 'MICRONUTRIENT TRACKING' },
    { id: 'hydration', label: 'Hydration', sub: 'WATER INTAKE' },
    { id: 'timer', label: 'Smart Timer', sub: 'FASTING, SHIFT & TIMING' },
  ]},
  { tier: 'genius', items: [
    { id: 'health-intel', label: 'Health Intel', sub: 'AI INSIGHTS' },
    { id: 'smart-actions', label: 'Smart Actions', sub: 'AI MEALS & PICKS' },
    { id: 'smart-hub', label: 'Smart Hub', sub: 'SAVED & TRENDS' },
    { id: 'miq-status', label: 'MIQ', sub: 'METABOLIC IQ' },
  ]},
];

export const MIQ_SECTIONS = [
  { tier: 'genius', items: [
    { id: 'metabolic-guidance', label: 'Guidance', sub: 'AI COACHING' },
    { id: 'workout-planner', label: 'Workouts', sub: 'TRAINING PLANS' },
    { id: 'bloodwork', label: 'Bloodwork', sub: 'LAB RESULTS' },
    { id: 'peptide-calc', label: 'Peptides', sub: 'DOSE CALC' },
    { id: 'health-profile', label: 'Profile', sub: 'CONDITIONS' },
    { id: 'score-breakdown', label: 'Score', sub: 'MIQ FACTORS' },
    { id: 'reports', label: 'Reports', sub: 'BRIEFS & REPORTS' },
  ]},
];

export const SMARTHUB_SECTIONS = [
  { tier: 'genius', items: [
    { id: 'favorites', label: 'Favorites', sub: 'QUICK LOG' },
    { id: 'saved-recipes', label: 'Recipes', sub: 'SMART SWAPS' },
    { id: 'today-intake', label: 'Today', sub: 'DAILY SUMMARY' },
    { id: 'health-sync', label: 'Health Sync', sub: 'WEARABLES & VITALS' },
  ]},
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPASS COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const NavigationWheel = ({ 
  sections, 
  openSections, 
  onToggleSection, 
  userTier = 'free',
  onUpgrade,
  onCenterClick,
  centerLabel = 'SmartGoals',
  centerSub = 'SET YOUR PLAN'
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(330);
  
  const tierRank = { free: 0, smart: 1, genius: 2, miq: 2 };
  const userRank = tierRank[userTier] ?? 0;
  const openCount = openSections.size;

  // Measure container width for responsive sizing
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isExpanded]);

  const allItems = sections.flatMap(g => 
    g.items.map(item => ({ ...item, tier: g.tier }))
  );

  const handleToggle = useCallback((item) => {
    const isLocked = tierRank[item.tier] > userRank;
    if (isLocked) { onUpgrade?.(); return; }
    onToggleSection(item.id);
  }, [userRank, onToggleSection, onUpgrade]);

  // ── Collapsed State ──
  if (!isExpanded) {
    return (
      <button
        data-testid="nav-wheel-toggle"
        onClick={() => setIsExpanded(true)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          width: '100%', padding: '12px 16px',
          background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.06) 0%, rgba(59, 130, 246, 0.06) 100%)',
          border: '1px solid rgba(168, 85, 247, 0.15)',
          borderRadius: '14px', cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A855F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
        </svg>
        <span style={{ color: '#A855F7', fontSize: '13px', fontWeight: '600' }}>Feature Compass</span>
        {openCount > 0 && (
          <span style={{
            background: '#A855F7', color: '#fff', fontSize: '10px', fontWeight: '700',
            padding: '1px 6px', borderRadius: '10px', minWidth: '18px', textAlign: 'center'
          }}>{openCount}</span>
        )}
        <ChevronDown size={14} color="#A855F7" />
      </button>
    );
  }

  // ── Responsive sizing ──
  const ringRadius = Math.min(containerWidth * 0.24, 95);
  const labelOffset = Math.min(containerWidth * 0.15, 55);
  const totalRadius = ringRadius + labelOffset + 16;
  const containerHeight = totalRadius * 2 + 16;
  const hubSize = Math.min(ringRadius * 0.82, 72);
  const labelFontSize = containerWidth < 360 ? '9px' : '10px';
  const subFontSize = '6px';

  return (
    <div data-testid="nav-wheel" ref={containerRef} style={{
      background: 'rgba(6,6,16,0.85)',
      border: '1px solid rgba(255,255,255,0.04)',
      borderRadius: '16px',
      padding: '8px 8px 6px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Collapse × */}
      <button
        data-testid="nav-wheel-collapse"
        onClick={() => setIsExpanded(false)}
        style={{
          position: 'absolute', right: '10px', top: '10px',
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '50%', width: '24px', height: '24px',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10, color: colors.mutedText, fontSize: '14px', lineHeight: 1
        }}
      >×</button>

      {/* Circle Container */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: `${containerHeight}px`,
        maxWidth: `${totalRadius * 2}px`,
        margin: '0 auto'
      }}>
        {/* Outer Ring */}
        <div style={{
          position: 'absolute', left: '50%', top: '50%',
          width: `${ringRadius * 2}px`, height: `${ringRadius * 2}px`,
          borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(124,92,252,0.04) 0%, transparent 70%)'
        }} />

        {/* Inner Ring */}
        <div style={{
          position: 'absolute', left: '50%', top: '50%',
          width: `${ringRadius * 1.15}px`, height: `${ringRadius * 1.15}px`,
          borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)',
          transform: 'translate(-50%, -50%)'
        }} />

        {/* Center Hub — Health Intelligence */}
        <button
          data-testid="nav-wheel-center"
          onClick={() => onCenterClick ? onCenterClick() : setIsExpanded(false)}
          style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${hubSize}px`, height: `${hubSize}px`,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(20,20,30,1) 0%, rgba(10,10,18,1) 100%)',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 5, boxShadow: '0 0 24px rgba(124, 92, 252, 0.15)'
          }}
        >
          <span style={{ fontSize: '16px', marginBottom: '1px' }}>🎯</span>
          <span style={{ fontSize: '8px', fontWeight: '800', color: colors.text, letterSpacing: '0.3px' }}>{centerLabel}</span>
          <span style={{ fontSize: '6px', fontWeight: '700', color: '#A855F7', letterSpacing: '1px' }}>{centerSub}</span>
        </button>

        {/* SVG Segment Arcs */}
        <svg style={{
          position: 'absolute', left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          width: `${ringRadius * 2 + 4}px`, height: `${ringRadius * 2 + 4}px`,
          zIndex: 1
        }} viewBox={`0 0 ${ringRadius * 2 + 4} ${ringRadius * 2 + 4}`}>
          {allItems.map((item, i) => {
            const cx = ringRadius + 2;
            const cy = ringRadius + 2;
            const r = ringRadius - 2;
            const seg = 360 / allItems.length;
            const a1 = (seg * i - 90 - seg / 2) * (Math.PI / 180);
            const a2 = (seg * i - 90 + seg / 2) * (Math.PI / 180);
            const x1 = cx + r * Math.cos(a1);
            const y1 = cy + r * Math.sin(a1);
            const x2 = cx + r * Math.cos(a2);
            const y2 = cy + r * Math.sin(a2);
            const tc = TIER_META[item.tier]?.color || '#666';
            const active = openSections.has(item.id);
            const locked = tierRank[item.tier] > userRank;
            return (
              <path key={item.id}
                d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`}
                fill="none"
                stroke={active ? tc : (locked ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)')}
                strokeWidth={active ? 3 : 2}
                opacity={locked ? 0.3 : 0.7}
              />
            );
          })}
        </svg>

        {/* Compass Items */}
        {allItems.map((item, i) => {
          const angle = (360 / allItems.length) * i - 90;
          const rad = (angle * Math.PI) / 180;
          const isLocked = tierRank[item.tier] > userRank;
          const isActive = openSections.has(item.id);
          const tc = TIER_META[item.tier]?.color || '#666';

          // Dot on ring
          const dotX = (ringRadius - 6) * Math.cos(rad);
          const dotY = (ringRadius - 6) * Math.sin(rad);
          
          // Label positioned outside ring
          const lx = (ringRadius + labelOffset * 0.6) * Math.cos(rad);
          const ly = (ringRadius + labelOffset * 0.6) * Math.sin(rad);
          
          const isLeftSide = angle > 90 && angle < 270;
          const isNearTop = Math.abs(angle + 90) < 25 || Math.abs(angle - 270 + 360) < 25;
          const isNearBottom = Math.abs(angle - 90) < 25;
          const textAlign = (isNearTop || isNearBottom) ? 'center' : (isLeftSide ? 'right' : 'left');

          return (
            <React.Fragment key={item.id}>
              {/* Dot */}
              <div style={{
                position: 'absolute',
                left: `calc(50% + ${dotX}px)`, top: `calc(50% + ${dotY}px)`,
                width: isActive ? '8px' : '5px', height: isActive ? '8px' : '5px',
                borderRadius: '50%',
                background: isActive ? tc : (isLocked ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.25)'),
                boxShadow: isActive ? `0 0 6px ${tc}60` : 'none',
                transform: 'translate(-50%, -50%)', zIndex: 2,
                transition: 'all 0.15s ease'
              }} />

              {/* Label */}
              <button
                data-testid={`wheel-section-${item.id}`}
                onClick={() => handleToggle(item)}
                style={{
                  position: 'absolute',
                  left: `calc(50% + ${lx}px)`, top: `calc(50% + ${ly}px)`,
                  transform: 'translate(-50%, -50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '3px 1px', textAlign,
                  opacity: isLocked ? 0.35 : 1,
                  zIndex: 3, maxWidth: '72px',
                  transition: 'opacity 0.15s ease'
                }}
              >
                <div style={{
                  fontSize: labelFontSize, fontWeight: isActive ? '700' : '600',
                  color: isActive ? tc : colors.text,
                  lineHeight: '1.1', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: '2px',
                  justifyContent: textAlign === 'center' ? 'center' : (textAlign === 'right' ? 'flex-end' : 'flex-start'),
                }}>
                  {isActive && <Check size={8} strokeWidth={3} color={tc} />}
                  {isLocked && <Lock size={7} color={colors.mutedText} />}
                  {item.label}
                </div>
                <div style={{
                  fontSize: subFontSize, fontWeight: '600',
                  color: isActive ? `${tc}99` : colors.mutedText,
                  letterSpacing: '0.4px', lineHeight: '1.2',
                  whiteSpace: 'nowrap', textAlign
                }}>
                  {item.sub}
                </div>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Tier Legend */}
      <div style={{
        display: 'flex', justifyContent: 'center', gap: '14px', padding: '0 16px 2px',
      }}>
        {Object.entries(TIER_META).map(([tier, meta]) => (
          <div key={tier} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: meta.color }} />
            <span style={{ fontSize: '8px', color: colors.mutedText, fontWeight: '600' }}>{meta.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK: Manage open sections with localStorage persistence
// ═══════════════════════════════════════════════════════════════════════════════
export const useWheelSections = (screenKey, userId) => {
  const storageKey = `wheel_sections_${screenKey}_${userId || 'guest'}`;
  const [openSections, setOpenSections] = useState(new Set());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) setOpenSections(new Set(JSON.parse(saved)));
      else setOpenSections(new Set());
    } catch { setOpenSections(new Set()); }
    setHydrated(true);
  }, [storageKey]);

  useEffect(() => {
    if (!hydrated) return;
    try { localStorage.setItem(storageKey, JSON.stringify([...openSections])); }
    catch { /* ignore */ }
  }, [openSections, storageKey, hydrated]);

  const toggleSection = useCallback((sectionId) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  return { openSections, toggleSection };
};

export default NavigationWheel;
